/**
 * Audio player component for meditation sessions
 * Syncs with global FixedAudioPlayer when playing the same session
 */
import { useRef, useEffect, useCallback } from 'react';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { useAudioPlayerStore, useLanguage } from '@/store';
import { cn, getAudioPlayerTranslation } from '@/utils';
import type { Session, LocalizedSession, SessionDisplay } from '@/types';

interface AudioPlayerProps {
  session: Session | LocalizedSession | SessionDisplay;
  onProgress?: (currentTime: number, duration: number) => void;
  onComplete?: () => void;
  onStart?: () => void;
  className?: string;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function AudioPlayer({
  session,
  onProgress,
  onComplete,
  onStart,
  className,
}: AudioPlayerProps) {
  const language = useLanguage();
  const progressRef = useRef<HTMLDivElement>(null);
  const hasCalledStart = useRef(false);

  const t = (key: Parameters<typeof getAudioPlayerTranslation>[0]) => getAudioPlayerTranslation(key, language);

  // Use global audio player store
  const {
    currentSession,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    playSession,
    setIsPlaying,
    setCurrentTime,
    setVolume,
    setIsMuted,
  } = useAudioPlayerStore();

  // Check if this session is the one currently playing globally
  const isCurrentSession = currentSession?.id === session.id;

  // Get the audio element from the FixedAudioPlayer (via global ref)
  const getAudioElement = useCallback((): HTMLAudioElement | null => {
    return document.querySelector('audio[data-global-player="true"]');
  }, []);

  // Start playing this session (switches to global player)
  const handlePlay = useCallback(() => {
    if (!isCurrentSession) {
      // Start playing this session in the global player
      playSession(session);
      hasCalledStart.current = false;
    } else {
      // Resume/pause the current session — keep position intact
      const audio = getAudioElement();
      if (audio) {
        if (isPlaying) {
          audio.pause();
        } else {
          audio.play().catch(() => {
            setIsPlaying(false);
          });
        }
      }
      setIsPlaying(!isPlaying);
    }
  }, [isCurrentSession, isPlaying, playSession, session, setIsPlaying, getAudioElement]);

  // Skip forward/backward
  const skip = useCallback((seconds: number) => {
    const audio = getAudioElement();
    if (audio && isCurrentSession) {
      audio.currentTime = Math.max(
        0,
        Math.min(audio.currentTime + seconds, duration)
      );
    }
  }, [duration, isCurrentSession, getAudioElement]);

  // Restart from beginning
  const restart = useCallback(() => {
    const audio = getAudioElement();
    if (audio && isCurrentSession) {
      audio.currentTime = 0;
      setCurrentTime(0);
    }
  }, [isCurrentSession, setCurrentTime, getAudioElement]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    const audio = getAudioElement();
    if (audio && isCurrentSession) {
      audio.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted, isCurrentSession, setIsMuted, getAudioElement]);

  // Handle volume change
  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    const audio = getAudioElement();
    if (audio && isCurrentSession) {
      audio.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  }, [isCurrentSession, setVolume, setIsMuted, getAudioElement]);

  // Seek to a position based on X coordinate relative to progress bar
  const seekToPosition = useCallback((clientX: number) => {
    if (!progressRef.current) return;
    const audio = getAudioElement();
    if (!audio || !isCurrentSession) return;

    const rect = progressRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const newTime = percent * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration, isCurrentSession, setCurrentTime, getAudioElement]);

  // Handle progress bar click (desktop)
  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    seekToPosition(e.clientX);
  }, [seekToPosition]);

  // Handle touch drag on progress bar (mobile)
  const handleProgressTouch = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    const touch = e.touches[0];
    if (touch) {
      seekToPosition(touch.clientX);
    }
  }, [seekToPosition]);

  // Call onStart when session starts playing
  useEffect(() => {
    if (isCurrentSession && isPlaying && !hasCalledStart.current) {
      hasCalledStart.current = true;
      onStart?.();
    }
  }, [isCurrentSession, isPlaying, onStart]);

  // Call onProgress when time updates
  useEffect(() => {
    if (isCurrentSession && duration > 0) {
      onProgress?.(currentTime, duration);
    }
  }, [isCurrentSession, currentTime, duration, onProgress]);

  // Call onComplete when session ends
  useEffect(() => {
    if (isCurrentSession && duration > 0 && currentTime >= duration - 0.5) {
      onComplete?.();
    }
  }, [isCurrentSession, currentTime, duration, onComplete]);

  // Calculate progress percentage
  const displayTime = isCurrentSession ? currentTime : 0;
  const displayDuration = isCurrentSession ? duration : session.duration;
  const displayIsPlaying = isCurrentSession ? isPlaying : false;
  const displayVolume = isCurrentSession ? volume : 1;
  const displayIsMuted = isCurrentSession ? isMuted : false;
  const progressPercent = displayDuration > 0 ? (displayTime / displayDuration) * 100 : 0;

  return (
    <div className={cn('bg-card rounded-xl p-4 sm:p-6 shadow-lg', className)}>
      {/* Progress bar — touch-friendly with larger hit area */}
      <div
        ref={progressRef}
        className="relative h-2 bg-muted rounded-full cursor-pointer mb-3 sm:mb-4 touch-none"
        onClick={handleProgressClick}
        onTouchStart={handleProgressTouch}
        onTouchMove={handleProgressTouch}
      >
        <div
          className="absolute h-full bg-primary rounded-full"
          style={{ width: `${progressPercent}%` }}
        />
        {/* Draggable handle — 24px touch target */}
        <div
          className="absolute h-6 w-6 bg-primary rounded-full -top-2 shadow-md"
          style={{ left: `calc(${progressPercent}% - 12px)` }}
        />
      </div>

      {/* Time display */}
      <div className="flex justify-between text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
        <span>{formatTime(displayTime)}</span>
        <span>{formatTime(displayDuration)}</span>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-2 sm:gap-4">
        {/* Restart */}
        <Button
          variant="ghost"
          size="icon"
          onClick={restart}
          className="h-11 w-11"
          aria-label={t('restart')}
          disabled={!isCurrentSession}
        >
          <RotateCcw className="h-4 w-4 sm:h-5 sm:w-5" />
        </Button>

        {/* Skip back 10s */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => skip(-10)}
          className="h-11 w-11"
          aria-label={t('skipBack')}
          disabled={!isCurrentSession}
        >
          <SkipBack className="h-4 w-4 sm:h-5 sm:w-5" />
        </Button>

        {/* Play/Pause */}
        <Button
          onClick={handlePlay}
          size="icon"
          className="h-12 w-12 sm:h-14 sm:w-14 rounded-full"
          aria-label={displayIsPlaying ? t('pause') : t('play')}
        >
          {displayIsPlaying ? (
            <Pause className="h-5 w-5 sm:h-6 sm:w-6" />
          ) : (
            <Play className="h-5 w-5 sm:h-6 sm:w-6 ml-0.5" />
          )}
        </Button>

        {/* Skip forward 10s */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => skip(10)}
          className="h-11 w-11"
          aria-label={t('skipForward')}
          disabled={!isCurrentSession}
        >
          <SkipForward className="h-4 w-4 sm:h-5 sm:w-5" />
        </Button>

        {/* Volume - hidden on mobile, visible on sm+ */}
        <div className="hidden sm:flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMute}
            className="h-11 w-11"
            aria-label={displayIsMuted ? t('unmute') : t('mute')}
            disabled={!isCurrentSession}
          >
            {displayIsMuted || displayVolume === 0 ? (
              <VolumeX className="h-5 w-5" />
            ) : (
              <Volume2 className="h-5 w-5" />
            )}
          </Button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={displayIsMuted ? 0 : displayVolume}
            onChange={handleVolumeChange}
            className="w-20 h-2 accent-primary"
            aria-label={t('volume')}
            disabled={!isCurrentSession}
          />
        </div>
      </div>
    </div>
  );
}
